import type { ReactNode } from 'react';
import { Icon, Tone } from '~/components/ui';
import { useT } from '~/i18n';
import {
  SUBSCORES,
  scoreColorVar,
  scoreHeadline,
  scoreLabel,
  scoreTone,
  type CVScoreDetail,
} from '../lib/cvScore';

/**
 * La nota de la hoja de vida y, sobre todo, QUÉ HACER con ella.
 *
 * El número solo no sirve: un 42 sin instrucciones es una humillación con
 * tipografía grande. Lo que convierte el puntaje en coaching es la lista de
 * abajo —cada hueco con su instrucción ejecutable hoy— y por eso ocupa más
 * espacio que el arco. El arco es el gancho; la lista es el producto.
 *
 * El color del arco es el semáforo de la marca, no una escala inventada: coral
 * es fricción (esto se cae si sale así), púrpura es lo que está en marcha, mint
 * es el dato confirmado. Mismo idioma que los estados de una postulación, para
 * que quien ya aprendió a leer la tabla no tenga que aprender otra cosa acá.
 */

interface Props {
  detail: CVScoreDetail;
  /** El rótulo de encima. El perfil y la revisión del screening no dicen lo mismo. */
  scope?: string;
  /** Lo que se ofrece para tapar los huecos: un botón, un enlace. Va al pie. */
  action?: ReactNode;
  /** Marco propio (`card`) o desnudo, para incrustarlo en una tarjeta existente. */
  bare?: boolean;
  /**
   * Qué pasa al tocar un hueco: el mismo camino que el botón del pie (volver al
   * cuestionario). Sin esto el hueco es solo texto. Con la lista larga el botón
   * queda muy abajo en el celular, y la gente tocaba el «+» de cada hueco
   * esperando que hiciera algo: «le dan en el más y no pasa nada».
   */
  onAddGap?: () => void;
}

const RADIUS = 34;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

function Gauge({ score }: { score: number }) {
  const t = useT('onboarding');
  const color = scoreColorVar(score);
  // El arco se dibuja siempre completo en el DOM y se recorta con el dash: así
  // la transición del CSS tiene algo que animar cuando la nota cambia después
  // de rehacer el CV, en vez de aparecer de golpe.
  const filled = (Math.max(0, Math.min(100, score)) / 100) * CIRCUMFERENCE;

  return (
    <div className="score__gauge" role="img" aria-label={t.cvScore.gauge(score)}>
      <svg viewBox="0 0 80 80" width="80" height="80" aria-hidden="true">
        <circle
          cx="40"
          cy="40"
          r={RADIUS}
          fill="none"
          stroke="var(--surface-sunk)"
          strokeWidth="7"
        />
        <circle
          cx="40"
          cy="40"
          r={RADIUS}
          fill="none"
          stroke={color}
          strokeWidth="7"
          strokeLinecap="round"
          strokeDasharray={`${filled} ${CIRCUMFERENCE}`}
          transform="rotate(-90 40 40)"
          className="score__arc"
        />
      </svg>
      <span className="score__num" style={{ color }}>
        {score}
      </span>
    </div>
  );
}

export default function CVScoreCard({ detail, scope, action, bare, onAddGap }: Props) {
  // useT también hace que `scoreHeadline`/`scoreLabel` (que leen el idioma al
  // llamarse) se vuelvan a pintar al cambiar de idioma.
  const t = useT('onboarding');
  const { score, missing, strengths } = detail;

  const body = (
    <>
      <div className="score__head">
        <Gauge score={score} />
        <div className="min-w-0">
          <span className="brand-label">{scope ?? t.cvScore.defaultScope}</span>
          <p className="score__headline">{scoreHeadline(detail)}</p>
          <div className="mt-2">
            <Tone tone={scoreTone(score)}>{scoreLabel(score)}</Tone>
          </div>
        </div>
      </div>

      {/* Los cuatro sub-puntajes. Barras y no números sueltos: lo que la persona
          necesita ver de un vistazo es CUÁL de los cuatro está corto. */}
      <ul className="score__bars">
        {SUBSCORES.map((key) => {
          const value = detail.subscores[key];
          const { label, hint } = t.cvScore.subscores[key];
          return (
            <li key={key}>
              <div className="score__bar-top">
                <span className="score__bar-label">{label}</span>
                <span className="score__bar-value">{value}</span>
              </div>
              <div className="score__track">
                <span
                  className="score__fill"
                  style={{ width: `${value}%`, background: scoreColorVar(value) }}
                />
              </div>
              <span className="score__bar-hint">{hint}</span>
            </li>
          );
        })}
      </ul>

      {missing.length > 0 && (
        <div className="score__gaps">
          <h3 className="brand-label">{t.cvScore.gapsTitle}</h3>
          <ul>
            {missing.map((gap, i) => {
              const content = (
                <>
                  <span className="score__gap-mark" aria-hidden="true">
                    <Icon name="plus" size={13} />
                  </span>
                  <div className="min-w-0">
                    <p className="score__gap-field">{gap.field}</p>
                    {/* La instrucción primero: es lo único que se puede ejecutar.
                        El porqué va debajo y en tinta apagada — explica, no manda. */}
                    <p className="score__gap-fix">{gap.how_to_fix}</p>
                    {gap.why_it_matters && <p className="score__gap-why">{gap.why_it_matters}</p>}
                    {onAddGap && <span className="score__gap-cta">{t.cvScore.addGap} →</span>}
                  </div>
                </>
              );
              return (
                <li key={`${gap.field}-${i}`} className={onAddGap ? 'score__gap--action' : undefined}>
                  {onAddGap ? (
                    <button type="button" className="score__gap-btn" onClick={onAddGap}>
                      {content}
                    </button>
                  ) : (
                    content
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {strengths.length > 0 && (
        <div className="score__wins">
          <h3 className="brand-label">{t.cvScore.winsTitle}</h3>
          <div className="flex flex-wrap gap-2 mt-2">
            {strengths.map((s, i) => (
              <span key={`${s}-${i}`} className="chip">
                <span style={{ color: 'var(--mint-ink)', display: 'inline-flex' }}>
                  <Icon name="check" size={13} />
                </span>
                {s}
              </span>
            ))}
          </div>
        </div>
      )}

      {action && <div className="score__act">{action}</div>}
    </>
  );

  if (bare) return <div className="score">{body}</div>;

  return (
    <div className="card score">
      <div className="card-body">{body}</div>
    </div>
  );
}
